import { MobileOfferCatalogEntry, MobileOfferStatus } from '@numeo/types'
import { MobilePlan } from '@numeo/types-core'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowRight, Plus, RefreshCw, TicketPercent } from 'lucide-react'
import { mobileOfferError, mobileOffersApi } from './api'
import { OfferButton, offerFieldClass, OfferNotice } from './ui'
import { modeLabel, offerDescription, offerLinkClass, offerPlans, planLabels } from './offerLabels'
import { StoreStatusBadge } from './StoreStatusBadge'

export function OfferLibrary() {
    const [offers, setOffers] = useState<MobileOfferCatalogEntry[]>([])
    const [plan, setPlan] = useState<MobilePlan | ''>('')
    const [status, setStatus] = useState<MobileOfferStatus | ''>('')
    const [search, setSearch] = useState('')
    const [query, setQuery] = useState('')
    const [cursors, setCursors] = useState<Array<string | undefined>>([undefined])
    const [next, setNext] = useState<string | null>(null)
    const [busy, setBusy] = useState(true)
    const [error, setError] = useState('')
    const sequence = useRef(0)
    const cursor = cursors[cursors.length - 1]
    const load = useCallback(async () => {
        const request = ++sequence.current
        setBusy(true)
        setError('')
        try {
            const page = await mobileOffersApi.list({ cursor, plan: plan || undefined, status: status || undefined, search: query || undefined })
            if (request !== sequence.current) return
            setOffers(page.items)
            setNext(page.nextCursor)
        } catch (failure) {
            if (request === sequence.current) {
                setOffers([])
                setNext(null)
                setError(mobileOfferError(failure))
            }
        } finally {
            if (request === sequence.current) setBusy(false)
        }
    }, [cursor, plan, status, query])
    useEffect(() => {
        void load()
        return () => {
            sequence.current += 1
        }
    }, [load])
    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
                <div>
                    <h2 className="text-lg font-semibold">Offer library</h2>
                    <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Gluon-created offers, reusable across customer assignments.</p>
                </div>
                <Link
                    to="new"
                    className="inline-flex min-h-10 items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 focus-visible:ring-2 focus-visible:ring-blue-600 dark:bg-blue-500 dark:hover:bg-blue-600"
                >
                    <Plus size={18} />
                    Create offer
                </Link>
            </div>
            <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-900">
                <div className="flex flex-wrap items-end gap-3 border-b border-slate-200 p-4 dark:border-slate-700">
                    <form
                        className="flex min-w-0 flex-1 items-end gap-2"
                        onSubmit={(event) => {
                            event.preventDefault()
                            setCursors([undefined])
                            setQuery(search.trim())
                        }}
                    >
                        <label className="min-w-0 flex-1 space-y-1 text-xs font-medium">
                            Search offers
                            <input
                                type="search"
                                placeholder="Plan, country or offer ID"
                                maxLength={100}
                                className={offerFieldClass}
                                value={search}
                                onChange={(event) => setSearch(event.target.value)}
                            />
                        </label>
                        <OfferButton variant="secondary" type="submit">
                            Search
                        </OfferButton>
                    </form>
                    <label className="space-y-1 text-xs font-medium">
                        Plan
                        <select
                            className={offerFieldClass}
                            value={plan}
                            onChange={(event) => {
                                setPlan(event.target.value as MobilePlan | '')
                                setCursors([undefined])
                            }}
                        >
                            <option value="">All plans</option>
                            {offerPlans.map((value) => (
                                <option key={value} value={value}>
                                    {planLabels[value]}
                                </option>
                            ))}
                        </select>
                    </label>
                    <label className="space-y-1 text-xs font-medium">
                        Status
                        <select
                            className={offerFieldClass}
                            value={status}
                            onChange={(event) => {
                                setStatus(event.target.value as MobileOfferStatus | '')
                                setCursors([undefined])
                            }}
                        >
                            <option value="">All statuses</option>
                            <option value="active">Active</option>
                            <option value="draft">Draft</option>
                            <option value="paused">Paused</option>
                        </select>
                    </label>
                    <OfferButton variant="ghost" aria-label="Refresh offers" disabled={busy} onClick={() => void load()}>
                        <RefreshCw size={18} />
                    </OfferButton>
                </div>
                {error && (
                    <div className="p-4">
                        <OfferNotice error>{error}</OfferNotice>
                    </div>
                )}
                {busy ? (
                    <div className="space-y-3 p-6" role="status" aria-label="Loading offers">
                        {[1, 2, 3].map((key) => (
                            <div key={key} className="h-14 animate-pulse rounded-lg bg-slate-100 dark:bg-slate-800" />
                        ))}
                    </div>
                ) : offers.length ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-left text-sm">
                            <caption className="sr-only">Gluon offer library</caption>
                            <thead className="bg-slate-50 text-xs text-slate-500 dark:bg-slate-800 dark:text-slate-400">
                                <tr>
                                    {['Offer', 'Countries', 'Google Play', 'App Store', 'Purchase mode', ''].map((heading, index) => (
                                        <th className="whitespace-nowrap px-4 py-3 font-medium" key={index}>
                                            {heading || <span className="sr-only">Actions</span>}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                                {offers.map((offer) => (
                                    <tr key={offer.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                                        <td className="px-4 py-4">
                                            <p className="font-semibold">
                                                {planLabels[offer.terms.plan]}{' '}
                                                <span className="ml-1 text-xs font-normal capitalize text-slate-500 dark:text-slate-400">{offer.status}</span>
                                            </p>
                                            <p className="mt-1 whitespace-nowrap text-xs text-slate-500 dark:text-slate-400">{offerDescription(offer.terms)}</p>
                                        </td>
                                        <td className="px-4 py-4" title={offer.terms.countries.join(', ')}>
                                            {offer.terms.countries.length > 3 ? `${offer.terms.countries.length} countries` : offer.terms.countries.join(', ')}
                                        </td>
                                        <td className="px-4 py-4">
                                            <StoreStatusBadge store={offer.stores.google} />
                                        </td>
                                        <td className="px-4 py-4">
                                            <StoreStatusBadge store={offer.stores.apple} />
                                        </td>
                                        <td className="whitespace-nowrap px-4 py-4 text-xs text-slate-500 dark:text-slate-400">{modeLabel(offer.terms)}</td>
                                        <td className="px-4 py-4">
                                            <Link
                                                aria-label={`View ${planLabels[offer.terms.plan]} ${offer.terms.percentOff}% offer`}
                                                to={`offers/${offer.id}`}
                                                className={offerLinkClass}
                                            >
                                                Details
                                                <ArrowRight size={16} />
                                            </Link>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    !error && (
                        <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
                            <TicketPercent size={32} className="text-slate-400 dark:text-slate-500" />
                            <h3 className="font-semibold">No offers found</h3>
                            <p className="text-sm text-slate-500 dark:text-slate-400">Change your filters or create your first offer.</p>
                        </div>
                    )
                )}
                <footer className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3 dark:border-slate-700 dark:text-slate-400">
                    <span className="text-xs text-slate-500 dark:text-slate-400">
                        Page {cursors.length} · {busy ? 'Loading' : `${offers.length} offers`}
                    </span>
                    <div className="flex gap-2">
                        <OfferButton variant="secondary" disabled={busy || cursors.length === 1} onClick={() => setCursors((values) => values.slice(0, -1))}>
                            Previous
                        </OfferButton>
                        <OfferButton variant="secondary" disabled={busy || !next} onClick={() => next && setCursors((values) => [...values, next])}>
                            Next
                        </OfferButton>
                    </div>
                </footer>
            </div>
        </div>
    )
}
